import { useEffect } from "react";
import "./ReactCarouselRuntime.css";

function configFor(swiper) {
  const host = swiper.closest(".banner-slider, .donate-swiper-shell, .testimonial-slider-gold, .project-slider, .icn-slider") || swiper;

  if (host.classList.contains("banner-slider")) {
    return { perView: () => 1, step: 1, autoplay: 5000, showArrows: false };
  }

  if (host.classList.contains("donate-swiper-shell") || swiper.classList.contains("mySwiper-tm")) {
    return {
      perView: () => (window.innerWidth >= 1024 ? 4 : window.innerWidth >= 768 ? 3 : 2),
      step: 1,
      autoplay: 3000,
      loop: true,
    };
  }

  if (host.classList.contains("testimonial-slider-gold")) {
    return {
      perView: () => (window.innerWidth >= 1200 ? 3 : window.innerWidth >= 768 ? 2 : 1),
      step: 1,
      autoplay: 4500,
    };
  }

  return {
    perView: () => (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 768 ? 2 : 1),
    step: 1,
    autoplay: 0,
  };
}

function ensureButton(host, direction) {
  const className = direction === "next" ? "swiper-button-next" : "swiper-button-prev";
  const existing = host.querySelector(":scope > ." + className);
  if (existing) return existing;

  const button = document.createElement("button");
  button.type = "button";
  button.className = className + " react-carousel-nav";
  button.dataset.reactCarouselCreated = "true";
  button.setAttribute("aria-label", direction === "next" ? "Next slide" : "Previous slide");
  host.appendChild(button);
  return button;
}

function enhanceSwiper(swiper) {
  if (swiper.closest(".banner-slider")) return undefined;
  const wrapper = swiper.querySelector(":scope > .swiper-wrapper");
  if (!wrapper || swiper.dataset.reactCarousel === "true") return undefined;

  let slides = Array.from(wrapper.children).filter((child) => child.classList.contains("swiper-slide"));
  const host = swiper.parentElement || swiper;
  const config = configFor(swiper);
  const visibleSlides = Math.max(1, config.perView());

  // A row that already fits on screen must stay still. Looping a full row
  // creates empty space before the cloned cards are reached.
  if (slides.length <= visibleSlides) {
    host.classList.add("react-carousel-static");
    return () => host.classList.remove("react-carousel-static");
  }

  swiper.dataset.reactCarousel = "true";
  const originalSlideCount = slides.length;
  const createdClones = [];

  if (config.loop && slides.length <= Math.max(1, config.perView())) {
    slides.forEach((slide) => {
      const clone = slide.cloneNode(true);
      clone.dataset.reactCarouselClone = "true";
      wrapper.appendChild(clone);
      createdClones.push(clone);
    });
    slides = Array.from(wrapper.children).filter((child) => child.classList.contains("swiper-slide"));
  }

  let current = 0;
  let timer;

  const update = () => {
    const perView = Math.max(1, Math.min(config.perView(), slides.length));
    const maxIndex = Math.max(0, slides.length - perView);
    current = Math.max(0, Math.min(current, maxIndex));
    const gap = swiper.classList.contains("mySwiper-tm") ? 24 : 30;
    const basis = "calc((100% - " + gap * (perView - 1) + "px) / " + perView + ")";

    wrapper.style.setProperty("--react-carousel-gap", gap + "px");
    slides.forEach((slide) => {
      slide.style.flex = "0 0 " + basis;
      slide.style.maxWidth = basis;
    });

    const slideWidth = slides[0]?.getBoundingClientRect().width || 0;
    const offset = current * (slideWidth + gap);
    wrapper.style.transform = "translate3d(-" + offset + "px, 0, 0)";
  };

  const goTo = (nextIndex) => {
    const perView = Math.max(1, Math.min(config.perView(), slides.length));
    const maxIndex = Math.max(0, slides.length - perView);
    const loopMaxIndex = config.loop ? Math.max(0, originalSlideCount - 1) : maxIndex;
    current = nextIndex > loopMaxIndex ? 0 : nextIndex < 0 ? loopMaxIndex : nextIndex;
    update();
  };

  const next = () => goTo(current + config.step);
  const prev = () => goTo(current - config.step);

  const nextButton = config.showArrows === false ? null : ensureButton(host, "next");
  const prevButton = config.showArrows === false ? null : ensureButton(host, "prev");

  if (config.showArrows === false) {
    host.querySelectorAll(":scope > .swiper-button-next, :scope > .swiper-button-prev").forEach((button) => {
      if (button.dataset.reactCarouselCreated === "true") button.remove();
    });
  }

  nextButton?.addEventListener("click", next);
  prevButton?.addEventListener("click", prev);
  window.addEventListener("resize", update);

  if (config.autoplay) {
    timer = window.setInterval(next, config.autoplay);
    swiper.addEventListener("mouseenter", () => window.clearInterval(timer));
    swiper.addEventListener("mouseleave", () => {
      timer = window.setInterval(next, config.autoplay);
    });
  }

  update();

  return () => {
    window.clearInterval(timer);
    nextButton?.removeEventListener("click", next);
    prevButton?.removeEventListener("click", prev);
    if (nextButton?.dataset.reactCarouselCreated === "true") nextButton.remove();
    if (prevButton?.dataset.reactCarouselCreated === "true") prevButton.remove();
    window.removeEventListener("resize", update);
    delete swiper.dataset.reactCarousel;
    wrapper.style.transform = "";
    wrapper.style.removeProperty("--react-carousel-gap");
    slides.forEach((slide) => {
      slide.style.flex = "";
      slide.style.maxWidth = "";
    });
    createdClones.forEach((clone) => clone.remove());
  };
}

export default function ReactCarouselRuntime() {
  useEffect(() => {
    let cleanups = [];

    const refreshCarousels = () => {
      cleanups.forEach((cleanup) => cleanup());
      cleanups = Array.from(document.querySelectorAll(".swiper"))
        .map(enhanceSwiper)
        .filter(Boolean);
    };

    refreshCarousels();
    window.addEventListener("react-carousel-refresh", refreshCarousels);

    return () => {
      window.removeEventListener("react-carousel-refresh", refreshCarousels);
      cleanups.forEach((cleanup) => cleanup());
    };
  }, []);

  return null;
}
